import React, { useState, useEffect, useRef } from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, Link} from 'react-router-dom';
import Icon from 'react-web-vector-icons';
import * as ROUTES from '../../../utils/routes';
import burger from '../../../assets/menu/hamburger-button.png';
import leftIcon from '../../../assets/small-left-icon.svg';
import * as userActions from "../../../redux/User/actions";
import {ext} from "../../../utils/general.js";
import logoutIconOn from '../../../assets/menu/logout-on.svg';
import formIcon from '../../../assets/menu/form.svg';
import licenseIcon from '../../../assets/menu/license.svg';
import Select from 'react-select';
import UserAvatar from "../../UserAvatar";
import moment from 'moment';
import SearchResultRow from "./SearchResultRow";
import * as heatmapActions from "../../../redux/Heatmap/actions.js";
import * as opportunityActions from "../../../redux/Opportunity/actions.js";
import * as programActions from "../../../redux/Program/actions.js";
import * as fundActions from "../../../redux/Funds/actions.js";
import * as marketingActions from "../../../redux/Marketing/actions.js";
import LoadingPanel from "../../LoadingPanel";
import constants from "../../../utils/constants.js";
import LicenseInformationModal from '../../LicenseInformationModal';

import './header.scss';

const Header = props => {
	const {title, headerLinkText, headerLinkTarget, hideRefresh} = props;
	const [loading, setLoading] = useState(false);
	const [showMenu, setShowMenu] = useState(false);
	const [selectedSearchOption, setSelectedSearchOption] = useState('All');
	const [searchTerm, setSearchTerm] = useState('');
	const [searchResults, setSearchResults] = useState([]);
	const [isDirty, setIsDirty] = useState(false);
	const [showLicenseModal, setShowLicenseModal] = useState(false);
	const [openSearchDropdown, setOpenSearchDropdown] = useState(false);
	const searchDropdownRef = useRef(null);

	const menuOpen = useSelector(state => state.user.menuOpen);
	const user = useSelector(state => state.user.user);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const lastUpdated = useSelector(state => state.opportunity).lastUpdated;

	const searchOptions = ['All', 'Opportunity', 'Heatmap', 'Program', 'Fund', 'Marketing'];

	useEffect(() => {
		const load = async () => {
			if(searchTerm === "") {
				setSearchResults([]);
			}
			else {
				setIsDirty(true);
				dispatch(userActions.runSearch(searchTerm.toLowerCase(), selectedSearchOption.toLowerCase(), updateSearchResults))
				setOpenSearchDropdown(true);
			}
		};
		load();
	}, [searchTerm, selectedSearchOption]);

	useEffect(() => {
		document.addEventListener('mousedown', handleSearchDropdownClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleSearchDropdownClickOutside);
		};
	}, []);

	const updateSearchResults = results => {
		setSearchResults(results);
	};

	const handleSearchChange = (event) => {
		setSelectedSearchOption(event.target.value);
	};

	const redirectToRequestAFeatureForm = () => {
		window.open(constants.REQUEST_A_FEATURE_FORM_URL, '_blank', 'noopener,noreferrer');
	}

	const viewLicenseInformation = () => {
		setShowLicenseModal(true);
		setShowMenu(false); // Close the dropdown menu
	}

	const onSignout = () => {
		dispatch(userActions.signOut(singOutDone));
	};

	const singOutDone = () => {
		const isInternalLogin = localStorage.getItem(constants.IS_INTERNAL_LOGIN) === 'true';

		if(isInternalLogin){
			window.location.href = '/internal';
			localStorage.setItem(constants.IS_INTERNAL_LOGIN, 'false')
		}
	};

	const updateUserAccount = async account => {
		await dispatch(userActions.switchAccount(account.label, accountSwitched))
	};

	const accountSwitched = async () => {
		await refreshData();
		navigate(ROUTES.HOME);
	};

	const refreshData = async () => {
		setLoading(true);
		await dispatch(opportunityActions.loadOpportunities([], true));
		await dispatch(heatmapActions.loadHeatmaps([], true));
		await dispatch(programActions.loadPrograms([], true));
		await dispatch(fundActions.loadFunds([], true));
		await dispatch(marketingActions.loadMarketing([], true));
		setLoading(false);
	};

	const rowClick = (route) => {
		setSearchTerm('');
		setIsDirty(false);
		setOpenSearchDropdown(false);
		navigate(route);
	};

	const handleSearchDropdownClickOutside = (event) => {
		const isOutside = Array.isArray(searchDropdownRef)
			? searchDropdownRef
					.filter(r => Boolean(r.current))
					.every(r => r.current && !r.current.contains(event.target))
			: searchDropdownRef.current && !searchDropdownRef.current.contains(event.target)

		if (isOutside) {
			setOpenSearchDropdown(false);
		}
	};

	const handleClickToOpenSearchDropdown = () => {
		if(searchTerm !== '') {
			setOpenSearchDropdown(true);
		}
	}

	const highlightTerm = (line, term) => {
		const termEscaped = term.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
		const regex = new RegExp(`(${termEscaped})`, 'gi');
		const parts = line.split(regex);

		return (
			<div style={{whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}} title={line}>
				{parts.map((part, index) => (
					<React.Fragment key={index}>
						{regex.test(part) ? <strong style={{color: 'black'}}>{part}</strong> : part}
					</React.Fragment>
				))}
			</div>
		);
	};

	const headerContent = () => {
		return (
			<div className="header-right-items">

				{ switchAccount() }

				<div className="header-right-search-container">
					<div className="header-right-search-results-container">
						<div className="header-right-search-results-input-area">
							<select className="header-right-search-results-select" value={selectedSearchOption} onChange={handleSearchChange}>
								{ searchOptions.map((opt) => (
									<option key={opt} value={opt}>{opt}</option>
								))}
							</select>

							<div className="header-right-search-results-pipe"></div>

							<Icon name="search1" font="AntDesign" color="#88ABB4" style={{padding:'0', fontSize: '13px', marginBottom: '-1px'}} />
							<input type="text"
											value={searchTerm}
											onChange={e => setSearchTerm(e.target.value)}
											onClick={handleClickToOpenSearchDropdown}
											className="header-right-search-input"
											placeholder="Search..." />
						</div>

						{openSearchDropdown && (
							<div ref={searchDropdownRef} className="header-right-search-results-dropdown">
								{isDirty && searchResults.length === 0 && (
									<div style={{textAlign: 'center'}}>No Results</div>
								)}

								{
									searchResults.map((r, i) => (
										<SearchResultRow key={i} onClick={rowClick} to={r.route}>
											<div style={{width: '90px'}}>{r.Type}:</div>
											{highlightTerm(r.Name, searchTerm)}
										</SearchResultRow>
									))
								}
							</div>
						)}
					</div>
				</div>

				<div style={{fontSize: '14px'}}>
					<div style={{color: '#142A31', paddingBottom: '5px'}}>
						{user.displayName.split(" ")[0]}
					</div>
					<div style={{color: '#88ABB4'}}>
						{user.accountName}
					</div>
				</div>

				<UserAvatar name={user.displayName} />

				<div className="header-menu-icon">
					<div onClick={() => setShowMenu(!showMenu)} style={{cursor: 'pointer'}}>
						<Icon name="chevron-small-down" font="Entypo" color="#497480" />
					</div>

					{
						showMenu &&
						<div className="header-menu">
							<div className="header-menu-item" onClick={redirectToRequestAFeatureForm}>
								<img src={formIcon} style={{width: '16px'}} />
								Request a feature
							</div>
							<div className="header-menu-item" onClick={viewLicenseInformation}>
								<img src={licenseIcon} style={{width: '16px'}} />
								License Information
							</div>
							<div className="header-menu-item" onClick={onSignout}>
								<img src={logoutIconOn} style={{width: '16px'}} />
								Log out
							</div>
						</div>
					}

				</div>
			</div>
		)
	};

	const switchAccount = () => {
		if(ext(user)) {
			return <div/>;
		}

		const accounts = [ ...user.customers];
		const options = accounts
			.sort((a,b) => a.Name.localeCompare(b.Name))
			.map(account => ({ value: account.Id.trim(), label: account.Name.trim() }));

			const val = options.find(op => op.label === user.accountName);

		return (
			<div style={{width: '250px', textAlign: 'left'}}>
				<Select
					styles={{
						control: (baseStyles, state) => ({
							...baseStyles,
							border: '1px solid #D3E1ED !important',
							borderShadow: '0px 0px 0px 0px transparent',
							borderRadius: '45px',
							backgroundColor: 'transparent',
							paddingLeft: '5px',
							minHeight: '33px',
        			height: '33px',
							outline: 'none',
						}),
						indicatorsContainer: (baseStyles, state) => ({
							...baseStyles,
							height: '33px',
						}),
						valueContainer: (baseStyles, state) => ({
							...baseStyles,
							height: '33px',
						}),
						singleValue: (baseStyles, state) => ({
							...baseStyles,
							color: '#6A8991',
							fontSize: '14px',
						}),
					}}
					value={val}
					placeholder={"Switch Account"}
					onChange={updateUserAccount}
					options={options}
				/>
			</div>
		)
	};

	return (
		<div className="header">
			<div className="top">

				{ loading && <LoadingPanel hide /> }

				<div style={{display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%'}}>
					{
						title &&
						<div className="pageBaseHeaderContainer">

							{
								!menuOpen &&
								<div onClick={() => dispatch(userActions.toggleMenu())}>
									<img src={burger} style={{cursor: 'pointer', paddingRight: '40px'}} alt="" />
								</div>
							}

							<div className="pageBaseTitleContainer">
								{
									headerLinkText &&
									<Link to={headerLinkTarget}>
										<div className="pageBaseTitleLink">
											<img src={leftIcon} style={{width: '10px'}} />
											{headerLinkText}
										</div>
									</Link>
								}
								<div style={{display: 'flex', alignItems: 'center'}}>
									<div className="pageBaseTitle">{ title }</div>

									{
										hideRefresh ?
											<div/> :
											<div className="pageBaseRefresh" onClick={refreshData}>
												<Icon name="refresh-ccw" color="#6A8991" font="Feather" size={14}/>
												&nbsp;&nbsp;&nbsp;
												{
													lastUpdated ?
														`Refreshed ${moment(lastUpdated).format(constants.DATETIME_FOTMAT)}`
														:
														`Refresh`
												}

											</div>
									}
								</div>
							</div>
						</div>
					}
					{ headerContent() }
				</div>
			</div>
			
			{/* Add License Information Modal */}
			<LicenseInformationModal 
				visible={showLicenseModal}
				onClose={() => setShowLicenseModal(false)}
			/>
		</div>
	)
};

export default Header;
